import { useEnvironmentsContext, WbButton } from '@agilelab/plugin-wb-platform';
import {
  Environment,
  snakeCaseToTitleCase,
} from '@agilelab/plugin-wb-platform-common';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  makeStyles,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { ChangeEvent, MouseEvent, useState } from 'react';
import { useDeployRequest } from '../../../../hooks';
import { Action } from './types';

const useStyles = makeStyles(theme => ({
  success: {
    background: theme.palette.success.main,
    '&:hover': {
      background: theme.palette.success.main,
    },
  },
  error: {
    background: theme.palette.error.main,
    '&:hover': {
      background: theme.palette.error.main,
    },
  },
}));

interface DeployUndeployDialogComponentProps {
  open: { state: boolean; action?: Action; version?: string };
  onClose: (env?: Environment, provisionAndNavigate?: boolean) => void;
  envList?: Environment[];
  /** Entity name of the release, used to check deploy approval status. */
  releaseEntityName: string;
}

export function DeployUndeployDialogComponent({
  open,
  onClose,
  envList,
  releaseEntityName,
}: DeployUndeployDialogComponentProps): JSX.Element {
  const { state, action, version } = open;
  const { environmentList } = useEnvironmentsContext();
  const [env, setEnv] = useState<string | undefined>(
    envList?.length === 1 ? envList[0].name : undefined,
  );
  const theme = useTheme();
  const classes = useStyles();

  const isDeployAction = action === Action.Deploy;

  const {
    deployButtonLabel,
    showWaitingForApproval,
    isRequestingDeploy,
    loading: approvalLoading,
    handleRequestDeploy,
    showRequestDeploy,
  } = useDeployRequest({
    releaseEntityName,
    // Only fetch approval status when env is selected and action is Deploy.
    environment: isDeployAction ? env : undefined,
  });

  const handleActionClick = async (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (isDeployAction && showRequestDeploy) {
      await handleRequestDeploy();
      return;
    }

    const environment = environmentList.find(e => e.name === env);
    onClose(environment, true);
  };

  const actionButtonLabel = isDeployAction
    ? deployButtonLabel
    : snakeCaseToTitleCase(action ?? '');

  const actionButtonDisabled =
    !env ||
    showWaitingForApproval ||
    (isDeployAction && approvalLoading) ||
    isRequestingDeploy;

  return (
    <Dialog
      open={state}
      onClick={e => {
        e.stopPropagation();
      }}
    >
      <DialogTitle style={{ backgroundColor: theme.palette.bkg.primary }}>
        <Typography> Quick Provisioning</Typography>
        <IconButton
          size="small"
          onClick={e => {
            e.stopPropagation();
            onClose();
          }}
          aria-label="close"
          style={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" style={{ gap: '16px' }}>
          <Typography variant="body1">
            You choose to{' '}
            <span style={{ fontWeight: 600 }}>
              {action} V{version}
            </span>
          </Typography>
          <FormControl>
            <Select
              displayEmpty
              variant="outlined"
              style={{
                width: '50%',
              }}
              renderValue={_ => (
                <Typography>
                  {snakeCaseToTitleCase(env ?? 'Select Environment')}
                </Typography>
              )}
              value={env}
              onChange={(e: ChangeEvent<{ value: unknown }>) => {
                setEnv(e.target.value as string);
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              {envList?.map(e => (
                <MenuItem value={e.name}>
                  {snakeCaseToTitleCase(e.name)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Divider />

          <Typography>
            Selecting this type of provisioning the basic settings will be
            applied. For more control over the provisioning process go to the
            release details.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <WbButton
          color="primary"
          size="small"
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
            onClose();
          }}
          variant="outlined"
          label="Cancel"
        />
        <WbButton
          disabled={actionButtonDisabled}
          loading={
            isRequestingDeploy || (isDeployAction && approvalLoading && !!env)
          }
          size="small"
          onClick={handleActionClick}
          className={isDeployAction ? classes.success : classes.error}
          label={actionButtonLabel}
        />
      </DialogActions>
    </Dialog>
  );
}
